import { useEffect, useRef, useState } from 'react';
import { Ruler } from '@/components/icons';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import type { Location } from '@/api/locations';
import {
  buildCalibrationHtml,
  buildLabelPrintHtml,
  escapeHtml,
  openPrintWindow,
  THERMAL_LABEL_PRESETS,
} from '@/lib/labelPrint';
import { LabelsPerRowToggle } from '@/components/ui/LabelsPerRowToggle';

interface Props {
  open: boolean;
  location: Location | null;
  onClose: () => void;
}

type LabelSize = typeof THERMAL_LABEL_PRESETS[number]['key'] | 'custom';
const SIZES = THERMAL_LABEL_PRESETS;

export function LocationLabelModal({ open, location, onClose }: Props) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [labelSize, setLabelSize] = useState<LabelSize>('60x40');
  const [perRow, setPerRow] = useState<1 | 2>(1);
  const [customW, setCustomW] = useState('60');
  const [customH, setCustomH] = useState('40');
  const [qty, setQty] = useState('1');

  const code = location?.code ?? '';

  useEffect(() => {
    if (!open || !code) return;
    if (barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, code, {
          format: 'CODE128', width: 1.8, height: 40, displayValue: false, margin: 4,
          background: '#ffffff', lineColor: '#000000',
        });
      } catch { /* invalid */ }
    }
    QRCode.toDataURL(code, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } })
      .then(setQrDataUrl);
  }, [open, code]);

  function cellHtml(fw: number, fh: number, barcodeUrl: string, qrUrl: string): string {
    if (!location) return '';
    // Scale everything off the label's short side against a 60×40 reference
    // (same rule as GRN labels) so smaller rolls shrink instead of clipping.
    const s = Math.min(fw / 60, fh / 40);
    const baseFs = Math.max(4, 7 * s);
    const qrSide = fh * 0.55;
    const bcHeight = fh * 0.22;
    const codeFs = code.length > 22 ? baseFs + 2 : code.length > 14 ? baseFs + 4 : baseFs + 6;
    return `
<div class="cell">
  <div class="top-row">
    <div class="info">
      <div class="loc-level" style="font-size:${baseFs.toFixed(2)}pt">${escapeHtml(location.level)}</div>
      <div class="loc-code" style="font-size:${codeFs.toFixed(2)}pt">${escapeHtml(code)}</div>
      <div class="loc-name" style="font-size:${(baseFs + 1).toFixed(2)}pt">${escapeHtml(location.name)}</div>
    </div>
    <div class="qr-block">
      <img src="${qrUrl}" style="width:${qrSide.toFixed(2)}mm;height:${qrSide.toFixed(2)}mm" />
    </div>
  </div>
  <div class="barcode-row"><img src="${barcodeUrl}" style="max-height:${bcHeight.toFixed(2)}mm" /></div>
</div>`;
  }

  function resolvedSize() {
    const w = labelSize === 'custom' ? Math.max(10, Number(customW) || 60) : SIZES.find((s) => s.key === labelSize)!.w;
    const h = labelSize === 'custom' ? Math.max(10, Number(customH) || 40) : SIZES.find((s) => s.key === labelSize)!.h;
    // "2 labels" splits the chosen size into two equal labels side by side.
    const across = perRow;
    return { w: w / across, h, across, fullW: w };
  }

  function printCalibration() {
    const { w, h, across } = resolvedSize();
    openPrintWindow(buildCalibrationHtml({ w, h, across }), w, h);
  }

  async function printLabel() {
    if (!location) return;
    const { w, h, across } = resolvedSize();
    const copies = Math.max(1, Math.min(500, Number(qty) || 1));

    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    let barcodeUrl = '';
    try {
      JsBarcode(svgEl, code, { format: 'CODE128', width: 1.8, height: 40, displayValue: false, margin: 4, background: '#ffffff', lineColor: '#000000' });
      barcodeUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svgEl));
    } catch { /* skip */ }
    const qrUrl = await QRCode.toDataURL(code, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } });

    const cells: string[] = [];
    for (let i = 0; i < copies; i++) cells.push(cellHtml(w, h, barcodeUrl, qrUrl));

    const extraCss = `
      .loc-level { font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.06em; }
      .loc-name  { color: #333; line-height: 1.25; overflow-wrap: break-word; word-break: break-word; }
      .loc-code  { font-weight: 900; line-height: 1.1; overflow-wrap: break-word; word-break: break-all; }
    `;
    const html = buildLabelPrintHtml({
      w, h, across, extraCss,
      title: `Location — ${code}`,
      cellsHtml: cells.join(''),
    });
    openPrintWindow(html, w, h);
  }

  if (!open || !location) return null;

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm"
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--modal-bg)]"
      >
        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <div>
            <div className="text-sm font-extrabold text-[var(--text-primary)]">Location Label</div>
            <div className="text-[11px] font-mono text-[var(--text-muted)]">{code} · {location.name}</div>
          </div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100 bg-[var(--bg-soft)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 border-b border-[var(--border-color)] px-5 py-2 bg-[var(--bg-card)]">
          <span className="text-[11px] font-bold text-[var(--text-secondary)]">Set Quantity</span>
          <input
            type="number" min="1" max="500"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            className="w-16 rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] px-2 py-1 text-xs text-center font-bold text-[var(--text-primary)]"
          />
        </div>

        <div className="flex flex-col items-center px-6 py-5 gap-5">
          <div className="bg-white text-black font-sans shadow-lg" style={{ width: 300, border: '1.5px solid #444', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{location.level}</div>
                <div style={{ fontSize: code.length > 22 ? 14 : code.length > 14 ? 18 : 22, fontWeight: 900, lineHeight: 1.1, overflowWrap: 'break-word', wordBreak: 'break-all' }}>{code}</div>
                <div style={{ fontSize: 10, color: '#444', lineHeight: 1.25, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{location.name}</div>
              </div>
              <div style={{ flexShrink: 0 }}>
                {qrDataUrl
                  ? <img src={qrDataUrl} alt="QR" style={{ width: 90, height: 90 }} />
                  : <div style={{ width: 90, height: 90, background: '#eee', borderRadius: 4 }} />
                }
              </div>
            </div>
            <div style={{ borderTop: '1px solid #eee', paddingTop: 4 }}>
              <svg ref={barcodeRef} style={{ width: '100%', display: 'block' }} />
            </div>
          </div>

          <div className="w-full">
            <div className="mb-1.5 text-[11px] font-bold text-[var(--text-secondary)]">Print Size</div>
            <div className="grid grid-cols-2 gap-2">
              {SIZES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setLabelSize(s.key)}
                  className="rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                  style={{
                    background: labelSize === s.key ? 'var(--accent)' : 'var(--bg-card)',
                    color: labelSize === s.key ? '#000' : 'var(--text-secondary)',
                    borderColor: labelSize === s.key ? 'var(--accent)' : 'var(--border-color)',
                  }}
                >
                  {s.label}
                </button>
              ))}
              <button
                onClick={() => setLabelSize('custom')}
                className="rounded-lg px-3 py-2 text-[11px] font-bold border transition col-span-2"
                style={{
                  background: labelSize === 'custom' ? 'var(--accent)' : 'var(--bg-card)',
                  color: labelSize === 'custom' ? '#000' : 'var(--text-secondary)',
                  borderColor: labelSize === 'custom' ? 'var(--accent)' : 'var(--border-color)',
                }}
              >
                Custom size
              </button>
            </div>
            {labelSize === 'custom' && (
              <div className="mt-2 flex items-center gap-2">
                <input type="number" min="10" max="300" value={customW} onChange={(e) => setCustomW(e.target.value)}
                  className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]"
                  placeholder="W" />
                <span className="text-xs text-[var(--text-muted)]">mm ×</span>
                <input type="number" min="10" max="300" value={customH} onChange={(e) => setCustomH(e.target.value)}
                  className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]"
                  placeholder="H" />
                <span className="text-xs text-[var(--text-muted)]">mm</span>
              </div>
            )}
            <LabelsPerRowToggle value={perRow} onChange={setPerRow} w={resolvedSize().fullW} h={resolvedSize().h} />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <button onClick={onClose} className="rounded-lg bg-[var(--bg-soft)] border border-[var(--border-color)] px-4 py-1.5 text-xs font-bold text-[var(--text-secondary)]">Close</button>
          <button
            onClick={printCalibration}
            title="Prints one sized rectangle with corner marks + mm rulers so you can hold it against a real sticker to verify the size"
            className="rounded-lg bg-warning/20 border border-warning/50 px-4 py-1.5 text-xs font-bold text-warning"
          >
            <Ruler size={13} className="mr-1 inline-block align-[-2px]" /> Calibration
          </button>
          <button onClick={printLabel} className="rounded-lg bg-accent px-5 py-1.5 text-xs font-extrabold text-black">Print Label</button>
        </div>
      </div>
    </div>
  );
}
